import React, { useEffect, useRef } from 'react';

import { Box, Button, Grid, Typography, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import CancelIcon from '@mui/icons-material/Cancel';

import { useHistory } from 'react-router';
import { FLIGHTS } from '@shared/helpers/routes';
import sxStyles from './sxStyles';
import { ID_SITE } from '@shared/conf/env';

const ID_SITE_CONST = Number(ID_SITE);

const PARTIAL_EMISSION_MESSAGE_BY_SITE = {
  1: 'Comunicate a la brevedad con tu punto de venta seleccionado',
  2: 'Comunicate a la brevedad con tu vendedor de GIT de confianza',
};

const getPartialEmissionMessage = idSite =>
  PARTIAL_EMISSION_MESSAGE_BY_SITE[idSite] ??
  'Comunicate a la brevedad con el punto de contacto correspondiente';

const SubsidiaryInfo = ({ subsidiary, sx }) => {
  if (!subsidiary) return null;

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Typography sx={{ ...sx.typography, mb: 0 }}>
        <strong>{subsidiary.name}</strong>
      </Typography>

      <Typography sx={{ ...sx.typography, mb: 0 }}>
        {`${subsidiary.city} - ${subsidiary.address}`}
      </Typography>

      <Typography sx={{ ...sx.typography, mb: 0 }}>
        Tel.: {subsidiary.phone} | Email: {subsidiary.email}
      </Typography>
    </Box>
  );
};

const ReservationHeader = ({ code, email, emission, datos, ndc_code }) => {
  const theme = useTheme();
  const sx = sxStyles(theme);
  const history = useHistory();
  const aboutSectionRef = useRef(null);

  const isError = emission?.emission_result?.error_status === 400;
  const subsidiary = datos?.selectedSubsidiary;

  const isPartialEmission = emission?.emission_result?.partialEmission === true;

  // Scroll al montar
  useEffect(() => {
    window.scrollTo({
      top: aboutSectionRef.current?.offsetTop ?? 0,
      behavior: 'auto',
    });
  }, []);

  const handleGoToFlights = () => history.push(FLIGHTS);

  const partialMessage = getPartialEmissionMessage(ID_SITE_CONST);

  const statusConfig = {
    success: {
      icon: <CheckCircleIcon sx={sx.iconSuccess} />,
      title: (
        <Typography sx={sx.titleSuccess}>
          ¡Tu vuelo se {emission ? 'confirmó' : 'reservó'} exitosamente!
        </Typography>
      ),
      message: (
        <Typography sx={{ ...sx.typography, mt: 1, color: '#5e5e5e' }}>
          Te enviamos la reserva a {email}
        </Typography>
      ),
    },
    error: {
      icon: <CancelIcon sx={sx.iconError} />,
      title: (
        <Typography sx={sx.titleError}>
          Falló la emisión de tu ticket, sin embargo el pago se realizó
          correctamente
        </Typography>
      ),
      message: subsidiary && (
        <>
          <Typography sx={{ ...sx.typography, mt: 1 }}>
            Comunicate dentro de las 24h con tu punto de venta seleccionado
          </Typography>

          <SubsidiaryInfo subsidiary={subsidiary} sx={sx} />
        </>
      ),
    },
    partial: {
      icon: <WarningAmberIcon sx={sx.iconWarning} />,
      title: (
        <Typography sx={sx.titleWarning}>
          Tu vuelo fue emitido parcialmente
        </Typography>
      ),
      message: (
        <>
          <Typography sx={{ ...sx.typography, mt: 1 }}>
            {partialMessage}
          </Typography>

          <SubsidiaryInfo subsidiary={subsidiary} sx={sx} />
        </>
      ),
    },
  };

  // Selección del bloque actual
  let currentStatus = 'success';

  if (isError) {
    currentStatus = 'error';
  } else if (isPartialEmission) {
    currentStatus = 'partial';
  }

  const { icon, title, message } = statusConfig[currentStatus];

  /** Render de línea de código reutilizable */
  const renderCode = (label, value) =>
    value && (
      <Typography sx={sx.typography}>
        {label}:{' '}
        <Typography component="span" sx={sx.codeText}>
          {value}
        </Typography>
      </Typography>
    );

  return (
    <Grid container sx={sx.container} ref={aboutSectionRef}>
      {icon}
      {title}

      {renderCode('Código de reserva', code)}
      {renderCode('Código NDC', ndc_code)}

      {message}

      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button sx={sx.verItinerarioButton} onClick={handleGoToFlights}>
          Buscar un nuevo vuelo
        </Button>
      </Box>
    </Grid>
  );
};

export default ReservationHeader;
